import React, { Component } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { compose, graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { filter, propType } from 'graphql-anywhere';
import _ from 'lodash';
import MaskedInput from 'react-text-mask';

import {
  Button,
  ButtonToolbar,
  ControlLabel,
  FormControl,
  Modal,
  PanelGroup,
  Panel,
} from 'react-bootstrap';

import createAutoCorrectedDatePipe from '../../utils/createAutoCorrectedDatePipe';
import TimeEntryEditor from '../../components/TimeEntryEditor';
import TimeEntryViewer from '../../components/TimeEntryViewer';
import UserPicker from '../../components/UserPicker';
import {
  querySelector,
  parseErrors,
} from '../../utils';

import './style.css';
import queryMetaHandler from '../../utils/queryMetaHandler';

const propTypes = exact({
  itemId: PropTypes.number.isRequired, // TODO: Remove this prop in favor of selectedItem
  selectedItem: PropTypes.any.isRequired,
  onComplete: PropTypes.func.isRequired,

  data: PropTypes.object,
  // NOTE: date is fetched off props and set as the local state. It is then referenced as state.
  // eslint-disable-next-line react/no-unused-prop-types
  date: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  users: PropTypes.arrayOf(propType(UserPicker.fragments.user)).isRequired,
  crewTotalHours: PropTypes.string,

  crewTimesheetCreate: PropTypes.func,
  crewTimesheetDestroy: PropTypes.func,

  crewTimeEntryFormCreateOrUpdate: PropTypes.func,
  crewTimeEntryDestroy: PropTypes.func,
});


export class CrewTimeEntryEditor extends Component {
  static defaultState(props) {
    const { date } = props;
    return {
      selectedItem: null,
      errorMessage: null,
      date,
    };
  }

  constructor(props) {
    super(props);
    const initState = CrewTimeEntryEditor.defaultState(props);

    this.state = initState;
    this.key = 0;

    this.handleSelectItem = this.handleSelectItem.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleUserAdd = this.handleUserAdd.bind(this);
    this.handleUserRemove = this.handleUserRemove.bind(this);

    // eslint-disable-next-line max-len
    this.handleCrewTimeEntryFormCreateOrUpdate = this.handleCrewTimeEntryFormCreateOrUpdate.bind(this);
    this.handleCrewTimeEntryDelete = this.handleCrewTimeEntryDelete.bind(this);

    this.handleClearError = this.handleClearError.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Receiving new props means that we are editing a new submission. Reset to null.
    // Is there a way to accomplish this without overriding this lifecycle method?
    if (this.props.itemId !== nextProps.itemId) {
      const state = CrewTimeEntryEditor.defaultState(nextProps);
      this.setState(state);
    }
  }

  async handleUserAdd(user) {
    try {
      const variables = {
        input: {
          user_id: user.id,
          submission_item_id: this.props.itemId,
        },
      };
      await this.props.crewTimesheetCreate({ variables });
      await this.props.data.refetch();
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  async handleUserRemove(user) {
    try {
      const variables = {
        input: {
          user_id: user.id,
          submission_item_id: this.props.itemId,
        },
      };
      await this.props.crewTimesheetDestroy({ variables });
      await this.props.data.refetch();
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  async handleCrewTimeEntryFormCreateOrUpdate(item) {
    try {
      const variables = {
        input: {
          submission_item_id: this.props.itemId,
          time_entry_form: item,
        },
      };

      await this.props.crewTimeEntryFormCreateOrUpdate({ variables });
      await this.props.data.refetch();
      await this.setState({ selectedItem: null });
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  async handleCrewTimeEntryDelete(item) {
    try {
      const variables = {
        input: {
          key: item.key,
          submission_item_id: this.props.itemId,
        },
      };
      await this.props.crewTimeEntryDestroy({ variables });
      await this.props.data.refetch();
      this.key += 1;
      await this.setState({ selectedItem: null });
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  handleSelectItem(item) {
    const { key } = item;
    const selectedItem = _.find(this.props.items, { key });
    this.setState({ selectedItem });
  }

  handleClearError() {
    const errorMessage = null;
    this.setState({ errorMessage });
  }

  handleDateChange(e) {
    const { value } = e.target;
    this.setState({ date: value });
  }

  render() {
    const { users, items, crewTotalHours } = this.props;
    const { selectedItem, errorMessage, date } = this.state;

    const disableDate = (items.length !== 0);
    const disableEditor = (users.length === 0 || date === '');
    const disableViewer = (items.length === 0);

    const viewerItems = items.map((item) => {
      return filter(TimeEntryViewer.fragments.crewViewer, item);
    });

    let selectedViewerItem = null;
    let selectedEditorItem = null;
    if (selectedItem) {
      selectedViewerItem = filter(TimeEntryViewer.fragments.crewViewer, selectedItem);
      selectedEditorItem = filter(TimeEntryEditor.fragments.crewEditor, selectedItem);
    }

    return (
      <div className="crew-time-entry-editor-modal-container">
        <PanelGroup>
          <Panel>
            <ControlLabel>Date</ControlLabel>
            <FormControl
              disabled={disableDate}
              componentClass={MaskedInput}
              name="date"
              value={date}
              onChange={this.handleDateChange}
              type="text"
              placeholder="mm/dd/yyyy"
              mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
              pipe={createAutoCorrectedDatePipe('mm dd yyyy')}
            />
            <ControlLabel>Employees</ControlLabel>
            <UserPicker
              value={users}
              onAdd={this.handleUserAdd}
              onRemove={this.handleUserRemove}
            />
          </Panel>
          {disableEditor
          || (
          <Panel key={this.key}>
            <TimeEntryEditor
              item={selectedEditorItem}
              date={date}
              onCreate={this.handleCrewTimeEntryFormCreateOrUpdate}
              onUpdate={this.handleCrewTimeEntryFormCreateOrUpdate}
              onCancel={() => { this.setState({ selectedItem: null }); }}
            />
          </Panel>
          )}
          {disableViewer
          || (
          <Panel>
            <TimeEntryViewer
              items={viewerItems}
              selectedItem={selectedViewerItem}
              crewTotalHours={crewTotalHours}
              onSelect={this.handleSelectItem}
              onDelete={this.handleCrewTimeEntryDelete}
            />
          </Panel>
          )}
        </PanelGroup>
        <ButtonToolbar className="pull-right">
          <Button
            bsStyle="primary"
            onClick={() => { this.props.onComplete(this.props.selectedItem); }}
          >
            Complete
          </Button>
        </ButtonToolbar>

        <Modal
          show={!!errorMessage}
          onHide={this.handleClearError}
          container={this}
          backdrop={false}
          aria-labelledby="contained-modal-title"
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title">Error</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {errorMessage}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.handleClearError}>Close</Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

const AllCrewTimeEntriesQuery = gql`
  query AllCrewTimeEntries($itemId: Int!) {
    submission_item(id: $itemId) {
      id
      crew_total_hours {
        to_string
      }
      crew_time_entry_forms {
        ...TimeEntryEditor
        ...TimeEntryViewer
      }
      timesheets {
        edges {
          node {
            user {
              ...UserPickerUser
            }
          }
        }
      }
    }
  }
  ${TimeEntryEditor.fragments.crewEditor}
  ${TimeEntryViewer.fragments.crewViewer}
  ${UserPicker.fragments.user}
`;

/*
* input: CrewTimesheetCreateInput!
*   user_id: Int!
*   submission_item_id: Int!
*/
const CrewTimesheetCreateMutation = gql`
  mutation CrewTimesheetCreate($input: CrewTimesheetCreateInput!) {
    crew_timesheet_create(input: $input) {
      user {
        ...UserPickerUser
      }
    }
  }
  ${UserPicker.fragments.user}
`;


/*
* input: CrewTimesheetDestroy!
*   user_id: Int!
*   submission_item_id: Int!
*/

const CrewTimesheetDestroyMutation = gql`
  mutation CrewTimesheetDestroy($input: CrewTimesheetDestroyInput!) {
    crew_timesheet_destroy(input: $input)
  }
`;


/*
* input: CrewTimeEntryFormCreateOrUpdateInput!
*   submission_item_id: Int!
*   time_entry_form: TimeEntryFormInputType!
*     key: String
*     equipment_id: Int
*     ticket_number_id: Int
*     timezone: String
*     date: String
*     start_time: String
*     end_time: String
*     pto: Boolean
*     notes: String
*/
const CrewTimeEntryCreateOrUpdateMutation = gql`
  mutation CrewTimeEntryFormCreateOrUpdate($input: CrewTimeEntryFormCreateOrUpdateInput!) {
    crew_time_entry_form_create_or_update(input: $input) {
      ...TimeEntryEditor
    }
  }
  ${TimeEntryEditor.fragments.crewEditor}
`;

/*
* input: CrewTimeEntryDestroy!
*   submission_item_id: Int!
*   key: String!
*/

const CrewTimeEntryDestroyMutation = gql`
  mutation CrewTimeEntryDestroy($input: CrewTimeEntryDestroyInput!) {
    crew_time_entry_destroy(input: $input)
  }
`;

CrewTimeEntryEditor.propTypes = propTypes;

export const marshallData = ({ data, ownProps }) => {
  const items = querySelector(data, '$.submission_item.crew_time_entry_forms.*', []);
  const users = querySelector(data, '$.submission_item.timesheets.edges[*].node.user', []);

  let crewTotalHours = null;
  if (data.submission_item) {
    crewTotalHours = data.submission_item.crew_total_hours.to_string;
  }

  let date = '';
  if (items.length > 0) {
    const [headCrewTimeEntry] = items;
    const { date: headDate } = headCrewTimeEntry;
    date = headDate;
  }

  return {
    ...ownProps,
    data,
    date,
    items,
    users,
    crewTotalHours,
  };
};

/*
const withCrewTimesheetCreateMutation = graphql(CrewTimesheetCreateMutation, {
  name: 'crewTimesheetCreate',
  options: {
    refetchQueries: ['AllCrewTimeEntries'],
  },
});
*/

const withApi = compose(
  graphql(AllCrewTimeEntriesQuery, { props: marshallData, options: { fetchPolicy: 'network-only' } }),
  graphql(CrewTimesheetCreateMutation, { name: 'crewTimesheetCreate' }),
  graphql(CrewTimesheetDestroyMutation, { name: 'crewTimesheetDestroy' }),
  graphql(CrewTimeEntryCreateOrUpdateMutation, { name: 'crewTimeEntryFormCreateOrUpdate' }),
  graphql(CrewTimeEntryDestroyMutation, { name: 'crewTimeEntryDestroy' }),
  queryMetaHandler,
);

export default withApi(CrewTimeEntryEditor);
